import * as React from 'react';
import accounting from 'accounting';
import Slider from 'react-slick';
import Alert from 'src/components/common/Alert';
import Button from 'src/components/common/Button';
import IconMessage from 'src/components/common/IconMessage';
import FinalWarehouseCheck from 'src/components/common/FinalWarehouseCheck';
import ProductShipFromModal from 'src/components/common/ProductShipFromModal';

import cartService from 'src/services/cart/CartService';
import { IWarrantyProvider } from 'src/model/warranty.model';
import LocationModel from 'src/model/location.model';
import UserModel from 'src/model/user.model';

import defaultImage from '../../../assets/defaultImage';
import './PartDetailsModelMobile.scss';
import {
  ifLoggedIn,
  goToPartDetail,
  goToCart,
  cartUrl
} from 'src/utils/navigation';
import { getExtraStatus } from 'src/components/PartDetails/PartDetailsDesktop/PartDetailsDesktop';
import { IPartModel } from 'src/model/product.model';
import env from 'src/shared/env';
import { startEndWithWhiteSpacesValidation } from 'src/utils/whiteSpacesValidation';
import { linkLocationsToWarehouses } from 'src/utils/partsBusinessLogic';

interface IPartDetailsModelMobileProps {
  part: IPartModel;
  partAddress: any;
  showOnlyMSRP: boolean;
  isLoggedIn: boolean;
  cartDeliverLocationCode: string;
  cartDeliverLocation: LocationModel;
  cartDeliverMethod: string;
  cartItemsCount: number;
  userData: UserModel;
  ref: any;
  t: any;
  handleClose: () => void;
  updateCart: () => void;
  warranties: IWarrantyProvider[];
  getWarranties: () => void;
  scrollToTop: () => void;
}

interface IPartDetailsModelMobileState {
  qty: number;
  shipmentMarking: string;
  ticketNumber: string;
  warrantyProvider: string;
  warrantyProviderCode: string;
  claims: string;
  mainImage: string;
  showModalImage: boolean;
  showDropDown: boolean;
  showWarrantyInputs: boolean;
  showBackorderAlert: boolean;
  showCartAlert: boolean;
  cartAlertType: string;
  addToCartLoading: boolean;
  showShipmentAlert: boolean;
  showTicketNumberAlert: boolean;
  showDispatchClaimsAlert: boolean;
  cartAlertMessage: string;
  showShipFromModal: boolean;
  showFinalWarehouseCheck: boolean;
  shipFromOrder: object;
  shippingList: any[];
  noPickUpList: any[];
}

class PartDetailsModelMobile extends React.Component<
  IPartDetailsModelMobileProps,
  IPartDetailsModelMobileState
> {
  private ref: HTMLElement;

  constructor(props: any) {
    super(props);
    this.state = {
      qty: 1,
      shipmentMarking: '',
      ticketNumber: '',
      warrantyProvider: '',
      warrantyProviderCode: '',
      claims: '',
      mainImage: this.getMainImage(),
      showModalImage: false,
      showDropDown: false,
      showWarrantyInputs: false,
      showBackorderAlert: false,
      showCartAlert: false,
      cartAlertType: '',
      addToCartLoading: false,
      showShipmentAlert: false,
      showTicketNumberAlert: false,
      showDispatchClaimsAlert: false,
      cartAlertMessage: '',
      showShipFromModal: false,
      showFinalWarehouseCheck: false,
      shipFromOrder: {},
      shippingList: [],
      noPickUpList: []
    };
  }

  private getMainImage = () => {
    return this.props.part.images
      ? this.props.part.images[0]
        ? this.props.part.images[0]
        : defaultImage
      : defaultImage;
  };

  private handleInputQtyChange = (e: any) => {
    const warehouseQty =
      this.props.cartItemsCount === 0 ||
      this.props.cartDeliverMethod.toLocaleLowerCase() === 'shipping'
        ? this.props.part.warehouseQty
        : this.props.part.warehouses.find(
            warehouse =>
              this.props.cartDeliverLocationCode === warehouse.warehouseCode
          ).quantity;
    if (Number(e.target.value) > Number(warehouseQty)) {
      this.setState({ showBackorderAlert: true, qty: e.target.value });
    } else {
      this.setState({ showBackorderAlert: false, qty: e.target.value });
    }
  };

  private handleInputShipmentMarkingChange = (e: any) => {
    const shipmentMarking = e.target.value;

    if (
      !shipmentMarking ||
      (shipmentMarking !== ' ' &&
        !startEndWithWhiteSpacesValidation(shipmentMarking))
    ) {
      this.setState({
        shipmentMarking
      });

      if (shipmentMarking.length === 30) {
        this.setState({
          showShipmentAlert: true
        });
      } else {
        this.setState({
          showShipmentAlert: false
        });
      }
    }
  };

  private handleInputTicketNumberChange = (e: any) => {
    const ticketNumber = e.target.value;

    if (
      !ticketNumber ||
      (ticketNumber !== ' ' && !startEndWithWhiteSpacesValidation(ticketNumber))
    ) {
      this.setState({
        ticketNumber
      });

      if (ticketNumber.length === 10) {
        this.setState({
          showTicketNumberAlert: true
        });
      } else {
        this.setState({
          showTicketNumberAlert: false
        });
      }
    }
  };

  private handleSelectWarrantyProviderChange = (
    warranty: IWarrantyProvider
  ) => {
    this.setState({
      warrantyProviderCode: warranty.providerNumberString,
      warrantyProvider: warranty.warrantyClaimNumber
    });
  };

  private handleInputClaimsChange = (e: any) => {
    const claims = e.target.value;

    if (
      !claims ||
      (claims !== ' ' && !startEndWithWhiteSpacesValidation(claims))
    ) {
      this.setState({
        claims
      });

      if (claims.length >= 15) {
        this.setState({
          showDispatchClaimsAlert: true
        });
      } else {
        this.setState({
          showDispatchClaimsAlert: false
        });
      }
    }
  };

  private handleSubmit = (e: any) => {
    e.preventDefault();
    this.handleClickAddToCart();
  };

  private handleCloseAlert = () => {
    this.setState({ showBackorderAlert: false });
  };

  /* 
    buildCartAlert: Prepares data to show on cart Alert
   */
  private buildCartAlert = () => {
    const alertConfig = {
      message: '',
      icon: '',
      type: ''
    };
    switch (this.state.cartAlertType) {
      case 'success':
        alertConfig.message = 'common.itemsAddedToCart';
        alertConfig.icon = 'rpds-check-circle';
        alertConfig.type = 'success';
        break;

      case 'error':
        alertConfig.message = this.state.cartAlertMessage
          ? this.state.cartAlertMessage
          : 'common.errorHasOcurred';
        alertConfig.icon = 'rpds-error';
        alertConfig.type = 'error';
        break;

      case 'mixedCart':
        alertConfig.message = 'partDetails.cartMixedMethodProhibited';
        alertConfig.icon = 'rpds-error';
        alertConfig.type = 'error';
        break;
      case 'wrongShippingAddress':
        alertConfig.message = 'partDetails.cartWrongAddressProhibited';
        alertConfig.icon = 'rpds-error';
        alertConfig.type = 'error';
        break;

      default:
        break;
    }

    return alertConfig;
  };

  /* 
    handleOpenCartAlert
   */
  private handleOpenCartAlert = (
    type: 'success' | 'error' | 'mixedCart' | 'wrongShippingAddress',
    message?: string
  ) => {
    this.setState({
      showCartAlert: true,
      cartAlertType: type,
      cartAlertMessage: message ? message : ''
    });
    this.props.scrollToTop();
  };

  /* 
    handleCloseCartAlert : close add cart alert
   */
  private handleCloseCartAlert = () => {
    this.setState({
      showCartAlert: false,
      cartAlertType: '',
      cartAlertMessage: ''
    });
  };

  private handleShowDropDown = () => {
    this.setState(prevState => {
      return { showDropDown: !prevState.showDropDown };
    });
  };

  private handleShowWarrantyInputs = () => {
    this.setState(prevState => {
      return { showWarrantyInputs: !prevState.showWarrantyInputs };
    });
  };

  private handleOnClicDetails = () => {
    ifLoggedIn(() =>
      goToPartDetail(
        this.props.part.replacedPart
          ? `${
              this.props.part.replacedMfc
                ? this.props.part.replacedMfc + '  '
                : ''
            }${this.props.part.replacedPart}`
          : `${
              this.props.part.manufacturerCode
                ? this.props.part.manufacturerCode + '  '
                : ''
            }${this.props.part.productNumber}`
      )
    );
  };

  private handleShowChangeShipFrom = () => {
    this.setState({
      showShipFromModal: true,
      showFinalWarehouseCheck: false
    });
    window.scrollTo(0, 0);
  };

  private handleChangeShipFrom = (selectedAddresses: object) => {
    const newAddresses: string[] = [];
    let qty: number = 0;
    this.state.shippingList.forEach(address => {
      if (selectedAddresses.hasOwnProperty(address.warehouseCode)) {
        selectedAddresses[address.warehouseCode] = {
          ...selectedAddresses[address.warehouseCode],
          available: address.quantity
        };
        if (address.addressLine4) {
          newAddresses.push(
            `${address.addressLine4.toLocaleLowerCase()} ${address.stateCode}`
          );
        } else {
          newAddresses.push(`${address.name}`);
        }
      }
    });
    for (const key in selectedAddresses) {
      if (selectedAddresses.hasOwnProperty(key)) {
        qty += Number(selectedAddresses[key].qty);
      }
    }
    this.addToCart(selectedAddresses);
    this.setState({
      showShipFromModal: false,
      shipFromOrder: selectedAddresses,
      qty
    });
  };

  private handleCloseChangeShipFrom = () => {
    this.setState({ showShipFromModal: false });
  };

  private handleCloseFinalWarehouseCheck = () => {
    this.setState({ showFinalWarehouseCheck: false });
  };

  private linkWarehouses = (): any => {
    const list: any[] = linkLocationsToWarehouses(
      this.props.part.warehouses,
      this.props.part.locations
    );
    let noPickUpList: any[] = [];
    if (env.REACT_APP_BASE_ENVIRONMENT === 'CA') {
      noPickUpList = list;
    } else {
      list.forEach(element => {
        if (!element.pickUpOnly) {
          noPickUpList.push(element);
        }
      });
    }
    this.setState({ shippingList: list, noPickUpList });
  };

  private handleClickAddToCart = () => {
    if (
      (this.props.cartItemsCount === 0 && !this.props.userData.pickup) ||
      this.props.cartDeliverMethod.toLocaleLowerCase() === 'shipping'
    ) {
      if (this.state.showBackorderAlert) {
        this.linkWarehouses();
        if (
          this.props.part.state.toLowerCase() === 'available' &&
          this.props.part.state.toLowerCase() === 'in stock'
        ) {
          this.setState({
            showFinalWarehouseCheck: true
          });
        } else {
          this.addToCart({ [this.props.part.warehouseCode]: this.state.qty });
        }
      } else {
        this.addToCart({ [this.props.part.warehouseCode]: this.state.qty });
      }
    } else if (this.props.cartItemsCount === 0 && this.props.userData.pickup) {
      this.addToCart({ [this.props.part.warehouseCode]: this.state.qty });
    } else {
      this.addToCart({ [this.props.cartDeliverLocationCode]: this.state.qty });
    }
  };

  private addToCart = (shipFrom: object): void => {
    this.setState({ addToCartLoading: true });
    for (const key in shipFrom) {
      if (shipFrom.hasOwnProperty(key)) {
        cartService
          .addItem(
            this.state.claims,
            this.props.cartItemsCount === 0
              ? this.props.userData.pickup
                ? 'Pick-Up'
                : 'Shipping'
              : this.props.cartDeliverMethod,
            null, // this.props.partAddress.number,
            null,
            this.props.part.name,
            Number(shipFrom[key].qty) < this.state.qty
              ? parseInt(shipFrom[key].qty, 10)
              : this.state.qty,
            false,
            this.state.shipmentMarking,
            key,
            this.state.ticketNumber,
            this.state.warrantyProviderCode
          )
          .then(res => {
            this.handleOpenCartAlert('success');
            this.setState({ addToCartLoading: false });
            this.props.updateCart();
          })
          .catch(e => {
            if (e.toString().includes('code 409')) {
              if (
                e.response.data === 'Location does not match previous cart info'
              ) {
                this.handleOpenCartAlert('wrongShippingAddress');
              } else {
                this.handleOpenCartAlert('mixedCart');
              }
            } else {
              this.handleOpenCartAlert('error', e.response.data);
            }
            this.setState({ addToCartLoading: false });
          });
      }
    }
  };

  public componentDidUpdate(prevProps: IPartDetailsModelMobileProps) {
    if (prevProps.part.name !== this.props.part.name) {
      this.setState({
        showBackorderAlert: this.props.part.warehouseQty < 1,
        showCartAlert: false,
        mainImage: this.props.part.images
          ? this.props.part.images[0]
            ? this.props.part.images[0]
            : defaultImage
          : defaultImage,
        qty: 1,
        shipmentMarking: '',
        ticketNumber: '',
        claims: ''
      });
    }
  }

  public componentDidMount() {
    this.props.getWarranties();
    const warehouseQty =
      this.props.cartItemsCount === 0 ||
      this.props.cartDeliverMethod.toLocaleLowerCase() === 'shipping'
        ? this.props.part.warehouseQty
        : this.props.part.warehouses.find(
            warehouse =>
              this.props.cartDeliverLocationCode === warehouse.warehouseCode
          ).quantity;
    this.setState({
      showBackorderAlert: warehouseQty < 1,
      showCartAlert: false,
      mainImage: this.props.part.images
        ? this.props.part.images[0]
          ? this.props.part.images[0]
          : defaultImage
        : defaultImage,
      qty: 1,
      shipmentMarking: '',
      ticketNumber: '',
      claims: ''
    });
  }

  public render() {
    const { t } = this.props;

    const sliderSettings = {
      dots: true,
      infinite: true,
      speed: 500,
      slidesToShow: 1,
      slidesToScroll: 1
    };

    const alertConfig = this.buildCartAlert();
    const addCartButtonStatusDisabled =
      this.state.qty.toString() === '0' ||
      !this.state.qty ||
      this.state.addToCartLoading
        ? true
        : false;

    return (
      <div
        className="rp-part-details-model-mobile Roboto"
        ref={(ref: HTMLElement) => (this.ref = ref)}
      >
        <ProductShipFromModal
          show={this.state.showShipFromModal}
          onClose={this.handleCloseChangeShipFrom}
          type={'shipping'}
          defaultWarehouse={this.props.part.warehouseCode}
          supplierQuantity={Number(this.props.part.supplierQuantity)}
          deliverAddressesList={
            env.REACT_APP_BASE_ENVIRONMENT === 'CA'
              ? this.state.shippingList
              : this.state.noPickUpList
          }
          deliveryLocationCode={this.props.cartDeliverLocationCode}
          handleChangeShipFrom={this.handleChangeShipFrom}
          buttonVerbiage={'Add to cart'}
          selectedAddresses={
            this.props.cartItemsCount === 0 ||
            this.props.cartDeliverMethod.toLocaleLowerCase() === 'shipping'
              ? {
                  [this.props.part.warehouseCode]: {
                    qty: this.state.qty
                  }
                }
              : {
                  [this.props.cartDeliverLocationCode]: {
                    qty: this.state.qty
                  }
                }
          }
          selectedPartState={this.props.part.state}
          selectedPartQty = {this.state.qty}
        />
        {this.state.showFinalWarehouseCheck && (
          <FinalWarehouseCheck
            handleAddToCart={() =>
              this.addToCart({
                [this.props.part.warehouseCode]: this.state.qty
              })
            }
            onClose={this.handleCloseFinalWarehouseCheck}
            handleChangeWarehouse={this.handleShowChangeShipFrom}
          />
        )}
        <div className="rp-part-details-model-mobile--cart-alert">
          {this.state.showShipmentAlert && (
            <Alert
              styleClass="addToCartAlert"
              message={
                // 'Maximum allowable length for Shipment marking is 30 characters'
                `${t('productFieldErrors.maxShipmentCharacterErr', {
                  count: 30
                })}`
              }
              icon={'rpds-warning'}
              handleCloseAlert={() => {
                this.setState({
                  showShipmentAlert: false
                });
              }}
              type={'warning'}
            />
          )}
          {this.state.showTicketNumberAlert && (
            <Alert
              styleClass="addToCartAlert"
              message={
                // 'Maximum allowable length for Ticket/Dispatch number is 10 characters'
                `${t('productFieldErrors.maxTicketCharactersErr', {
                  count: 10
                })}`
              }
              icon={'rpds-warning'}
              handleCloseAlert={() => {
                this.setState({
                  showTicketNumberAlert: false
                });
              }}
              type={'warning'}
            />
          )}
          {this.state.showDispatchClaimsAlert && (
            <Alert
              styleClass="addToCartAlert"
              message={
                // 'Maximum allowable length for Dispatch/Claims marking is 15 characters'
                `${t('productFieldErrors.maxDispatchClaimsMarkingErr', {
                  count: 15
                })}`
              }
              icon={'rpds-warning'}
              handleCloseAlert={() => {
                this.setState({
                  showDispatchClaimsAlert: false
                });
              }}
              type={'warning'}
            />
          )}
          {this.state.showCartAlert ? (
            <Alert
              styleClass="addToCartAlert"
              message={t(alertConfig.message)}
              icon={alertConfig.icon}
              handleCloseAlert={this.handleCloseCartAlert}
              type={alertConfig.type}
            >
              <a
                href={cartUrl()}
                onClick={goToCart}
                className="goToCartLink"
              >{`${t('menu.goTo')} ${t('common.cart')}`}</a>
            </Alert>
          ) : (
            <React.Fragment />
          )}
        </div>
        <div
          className="rp-part-details-model-mobile--close-modal-icon"
          onClick={e => this.props.handleClose()}
        >
          <span>Close</span>
          <i className="rpds-close" />
        </div>
        <div className="rp-part-details-model-mobile--images">
          <Slider {...sliderSettings}>
            {this.props.part.images && this.props.part.images.length > 0 ? (
              this.props.part.images.map((image, i) => (
                <div key={i} className="image-container">
                  <div
                    className="image"
                    style={{
                      backgroundImage: `url(${image})`
                    }}
                  />
                </div>
              ))
            ) : (
              <div className="image-container">
                <div
                  className="image"
                  key={90}
                  style={{
                    backgroundImage: `url(${this.state.mainImage})`
                  }}
                />
              </div>
            )}
          </Slider>
        </div>
        <div className="rp-part-details-model-mobile--info">
          <p className="rp-part-details-model-mobile--info--name">
            <span>{this.props.part.name}</span>
            {this.props.part.specialOrder && (
              <span className="rp-part-details-model-mobile--info--special-order">
                <span className={`rpds-new-releases special-order-icon`} />
                <span>Special Order</span>
              </span>
            )}
          </p>
          <p className="rp-part-details-model-mobile--info--product-number">
            {this.props.part.productNumber}
          </p>
          <p className="rp-part-details-model-mobile--info--description">
            {this.props.part.description}
          </p>

          <div className="rp-part-details-model-mobile--info--prices-bar">
            {this.props.isLoggedIn && (
              <div className="rp-part-details-model-mobile--info--prices-bar-prices">
                <div className="row">
                  {!this.props.showOnlyMSRP && (
                    <div className="col">
                      <label className="rp-part-details-model-mobile--info--price-title">
                        Your price
                      </label>
                      <p className="rp-part-details-model-mobile--info--price">
                        {accounting.formatMoney(this.props.part.price)}
                      </p>
                    </div>
                  )}
                  <div className="col">
                    <label className="rp-part-details-model-mobile--info--price-title">
                      MSRP
                    </label>
                    <p className="rp-part-details-model-mobile--info--price">
                      {accounting.formatMoney(this.props.part.msrp)}
                    </p>
                  </div>
                </div>
              </div>
            )}
            <div className="rp-part-details-model-mobile--info--prices-bar-extra">
              <div className="rp-part-details-model-mobile--info--stock-text">
                <div className="row">
                  <div className="col">
                    <div>
                      <label className="rp-part-details-model-mobile--info--stock">
                        <span
                          className={
                            this.props.part.inStock
                              ? `rpds-check-circle stock-success-icon`
                              : `rpds-error stock-error-icon`
                          }
                        />
                        <span title={this.props.part.stateDescription}>
                          {this.props.part.state}
                        </span>
                        <IconMessage
                          icon={'rpds-error'}
                          message={this.props.part.stateDescription}
                          className={'rp-part-details-model-mobile-info-icon'}
                        />
                      </label>
                    </div>
                  </div>
                  {/* <div className="col rp-part-details-model-mobile--info--supplier">
                    <span className="rp-part-details-model-mobile--info--supplier--count">
                      {this.props.part.supplierQuantity}
                    </span>
                    <div>Manufacturer Quantity</div>
                  </div> */}
                </div>
                <div className="row">
                  <span className="col">
                    {// check if cart is empty or it is on shipping show default warehouse
                    this.props.cartItemsCount === 0 ||
                    this.props.cartDeliverMethod.toLocaleLowerCase() ===
                      'shipping'
                      ? this.props.part.warehouseDesc
                      : this.props.cartDeliverLocation.name.search(
                          ' PICK UP ONLY'
                        ) > 0 && env.REACT_APP_BASE_ENVIRONMENT === 'US'
                      ? this.props.cartDeliverLocation.name.slice(
                          0,
                          this.props.cartDeliverLocation.name.search(
                            ' PICK UP ONLY'
                          ) + 1
                        )
                      : this.props.cartDeliverLocation.name}
                  </span>
                </div>
                <div className="row">
                  <div className="col">
                    <div className="rp-part-details-model-mobile--info--extra">
                      {this.props.part.extraProducts.length > 0 && (
                        <span className="rp-part-details-model-mobile--info--extra-color">
                          {getExtraStatus(this.props.part.extraProducts)}
                        </span>
                      )}
                      {this.props.part.extraTotalAmount > 0 && (
                        <b className="rp-part-details-mobile--info--extra-text">
                          {env.REACT_APP_BASE_ENVIRONMENT === 'CA'
                            ? `An extra charge of ${accounting.formatMoney(
                                this.props.part.extraTotalAmount
                              )} may apply at checkout.`
                            : `An extra charge of ${accounting.formatMoney(
                                this.props.part.extraTotalAmount
                              )} will apply at checkout.`}
                        </b>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="rp-part-details-model--form">
          <form onSubmit={this.handleSubmit}>
            <div className="rp-part-details-model--form-qty-shipment">
              <div className="row">
                <div className="col-4 form-inputs">
                  <label>Qty:</label>
                  <input
                    name="qty"
                    type="number"
                    min="1"
                    value={this.state.qty}
                    onChange={this.handleInputQtyChange}
                  />
                </div>
                <div className="col form-inputs">
                  <label>Shipment marking:</label>
                  <input
                    name="shipmentMarking"
                    type="text"
                    value={this.state.shipmentMarking}
                    onChange={this.handleInputShipmentMarkingChange}
                    maxLength={30}
                  />
                </div>
                {env.REACT_APP_BASE_ENVIRONMENT === 'CA' && (
                  <div className="col form-inputs">
                    <label>Ticket / Dispatch #:</label>
                    <input
                      name="ticketNumber"
                      type="text"
                      value={this.state.ticketNumber}
                      onChange={this.handleInputTicketNumberChange}
                      maxLength={10}
                    />
                  </div>
                )}
              </div>
            </div>
            {this.state.showBackorderAlert && (
              <Alert
                type={'info'}
                styleClass={'rp-part-details-model-mobile--alert'}
                message={
                  env.REACT_APP_BASE_ENVIRONMENT === 'CA'
                    ? `${t('common.itemsBackOrderedOnly')}`
                    : `${this.state.qty -
                        this.props.part
                          .warehouseQty} item(s) will be backordered. We will ship them as soon as the stocks are available for shipment.`
                }
                icon={'rpds-shipping'}
                handleCloseAlert={this.handleCloseAlert}
              />
            )}
            <div className="rp-part-details-model-mobile--form--buttons">
              <Button
                width={'100%'}
                height={'56px'}
                buttonClass="primary-custom-button rp-part-details-model-mobile--form--button"
                label={t('common.addToCart')}
                icon={{ position: 'left', icon: 'rpds-shopping-cart' }}
                textTransform="uppercase"
                action={this.handleClickAddToCart}
                disabled={addCartButtonStatusDisabled}
              />
              <button className="secondary-button rp-part-details-model-mobile--form--button">
                Add to list
                <span className="rpds-expand-more rp-add-to-list-icon" />
              </button>
              <button
                className="secondary-button rp-part-details-model-mobile--form--button button-details"
                onClick={this.handleOnClicDetails}
              >
                More Info
              </button>
            </div>
            {this.props.warranties.length > 0 && (
              <React.Fragment>
                <div
                  className="rp-part-details-model-mobile--form-hidden-menu"
                  onClick={this.handleShowWarrantyInputs}
                >
                  Warranty Provider
                  <span
                    className={`${
                      this.state.showWarrantyInputs ? 'rpds-remove' : 'rpds-add'
                    } rp-add-icon`}
                  />
                </div>
                {env.REACT_APP_BASE_ENVIRONMENT === 'US' &&
                  this.state.showWarrantyInputs && (
                    <React.Fragment>
                      <div className="form-inputs">
                        <label>Add Warranty Provider:</label>
                        <div
                          onClick={this.handleShowDropDown}
                          className="combobox dropdown"
                        >
                          <div tabIndex={0} className="dropdown-box">
                            {this.state.warrantyProvider}
                          </div>
                          <div tabIndex={-1} className="downarrow">
                            <span className="rpds-expand-more" />
                          </div>
                          {this.state.showDropDown && (
                            <div className="dropdown-list">
                              {this.props.warranties.map(warranty => (
                                <div
                                  className="dropdown-list-option"
                                  key={warranty.providerNumber}
                                  onClick={e =>
                                    this.handleSelectWarrantyProviderChange(
                                      warranty
                                    )
                                  }
                                >
                                  {warranty.warrantyClaimNumber}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="form-inputs">
                        <label>Dispatch/Claims:</label>
                        <input
                          name="claims"
                          type="text"
                          value={this.state.claims}
                          onChange={this.handleInputClaimsChange}
                          maxLength={15}
                        />
                      </div>
                    </React.Fragment>
                  )}
              </React.Fragment>
            )}
          </form>
        </div>
      </div>
    );
  }
}

export default PartDetailsModelMobile;
